import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { ArrowLeft, Check, Sparkles, Volume2, Lock, Crown, X } from "lucide-react";
import { toast } from "sonner";
import { useTTS } from "../hooks/useTTS";
import { THEME_BUCKETS, findBucketForLesson } from "../lib/lessonThemes";
import { useSEO } from "../hooks/useSEO";
import LessonQuiz from "../components/LessonQuiz";

const LEVELS = ["N5", "N4", "N3"];

export const LessonsList = () => {
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTheme = searchParams.get("theme") || "";
  const activeLevel = searchParams.get("level") || "";

  useEffect(() => {
    api.get("/lessons").then((r) => setLessons(r.data.data)).catch(() => {});
  }, []);

  // Scroll to top when filter changes so the user sees the new results.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [activeTheme, activeLevel]);

  const activeBucket = useMemo(
    () => THEME_BUCKETS.find((b) => b.slug === activeTheme) || null,
    [activeTheme],
  );

  const filtered = useMemo(() => {
    return lessons.filter((l) => {
      if (activeLevel && l.level !== activeLevel) return false;
      if (activeBucket) {
        const bucket = findBucketForLesson(l);
        if (!bucket || bucket.slug !== activeBucket.slug) return false;
      }
      return true;
    });
  }, [lessons, activeLevel, activeBucket]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: false });
  };

  const clearAll = () => setSearchParams({}, { replace: false });
  const hasFilters = !!(activeTheme || activeLevel);

  const done = new Set(user?.completed_lessons || []);
  const isPremium = !!user?.is_premium;

  return (
    <div className="washi-bg min-h-screen" data-testid="lessons-list-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Bibliothèque</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] text-[#1A2A40] mb-3">
          Leçons. <span className="italic font-light">Une par jour.</span>
        </h1>
        <p className="text-[#4A5568] mb-8 max-w-xl">
          2 leçons offertes. <Link to="/pricing" className="text-[#E94A3F] font-bold underline">Passez Premium</Link> pour tout débloquer.
        </p>

        {/* Filters: level pills */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="lessons-level-filters">
          <span className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0] mr-1">Niveau :</span>
          <button
            onClick={() => setFilter("level", "")}
            data-testid="filter-level-all"
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${
              !activeLevel ? "bg-[#1A2A40] text-white border-[#1A2A40]" : "bg-white text-[#1A2A40] border-[#1A2A40]/20 hover:border-[#1A2A40]"
            }`}
          >
            Tous
          </button>
          {LEVELS.map((lv) => (
            <button
              key={lv}
              onClick={() => setFilter("level", lv === activeLevel ? "" : lv)}
              data-testid={`filter-level-${lv}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${
                activeLevel === lv ? "bg-[#E94A3F] text-white border-[#E94A3F]" : "bg-white text-[#1A2A40] border-[#1A2A40]/20 hover:border-[#E94A3F]"
              }`}
            >
              {lv}
            </button>
          ))}
        </div>

        {/* Filters: theme chips (12 buckets) */}
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="lessons-theme-filters">
          <span className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0] mr-1">Thème :</span>
          <button
            onClick={() => setFilter("theme", "")}
            data-testid="filter-theme-all"
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition ${
              !activeTheme ? "bg-[#1A2A40] text-white border-[#1A2A40]" : "bg-white text-[#1A2A40] border-[#1A2A40]/20 hover:border-[#1A2A40]"
            }`}
          >
            Tous
          </button>
          {THEME_BUCKETS.map((b) => (
            <button
              key={b.slug}
              onClick={() => setFilter("theme", b.slug === activeTheme ? "" : b.slug)}
              data-testid={`filter-theme-${b.slug}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition inline-flex items-center gap-1.5 ${
                activeTheme === b.slug
                  ? "bg-[#E94A3F] text-white border-[#E94A3F]"
                  : "bg-white text-[#1A2A40] border-[#1A2A40]/15 hover:border-[#E94A3F]"
              }`}
            >
              <span>{b.emoji}</span>
              <span>{b.name}</span>
              <span className={`text-[10px] font-black ${activeTheme === b.slug ? "text-white/80" : "text-[#A0AEC0]"}`}>
                {b.count}
              </span>
            </button>
          ))}
        </div>

        {/* Active filter summary + clear */}
        {hasFilters && (
          <div className="mb-6 flex items-center gap-3 text-sm" data-testid="lessons-filter-summary">
            <span className="text-[#4A5568]">
              <span className="font-bold text-[#1A2A40]">{filtered.length}</span> leçon{filtered.length > 1 ? "s" : ""}
              {activeBucket && <> dans <span className="font-bold">{activeBucket.emoji} {activeBucket.name}</span></>}
              {activeLevel && <> · niveau <span className="font-bold">{activeLevel}</span></>}
            </span>
            <button
              onClick={clearAll}
              data-testid="filter-clear"
              className="inline-flex items-center gap-1 text-xs font-bold text-[#E94A3F] hover:underline"
            >
              <X className="w-3 h-3" /> Tout effacer
            </button>
          </div>
        )}

        {filtered.length === 0 && lessons.length > 0 && (
          <div className="bg-white border-2 border-dashed border-[#1A2A40]/15 rounded-2xl p-10 text-center" data-testid="lessons-empty-state">
            <div className="text-4xl mb-3">🍵</div>
            <p className="text-[#4A5568] mb-4">Aucune leçon ne correspond à ces filtres.</p>
            <button onClick={clearAll} className="text-[#E94A3F] font-bold underline">Voir toutes les leçons</button>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((l) => {
            const locked = l.premium && !isPremium;
            const isDone = done.has(l.id);
            return (
              <Link
                key={l.id} to={`/lessons/${l.id}`}
                data-testid={`lesson-item-${l.id}`}
                className={`relative rounded-3xl p-7 border-2 hover-lift block ${
                  isDone ? "bg-[#8CA07A]/10 border-[#8CA07A]" :
                  locked ? "bg-[#F3F6F1] border-[#1A2A40]/10" :
                  "bg-[#FDFBF7] border-[#1A2A40]/10"
                }`}
              >
                {locked && (
                  <div className="absolute top-4 right-4 bg-[#1A2A40] text-[#FFD166] rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Premium
                  </div>
                )}
                <div className="flex items-start justify-between mb-4">
                  <div className={`text-5xl ${locked ? "opacity-40 grayscale" : ""}`}>{l.emoji}</div>
                  {isDone && <div className="bg-[#8CA07A] text-white rounded-full p-1.5"><Check className="w-4 h-4" /></div>}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#E94A3F] mb-1">{l.level} · {l.duration}</div>
                <div className={`font-display font-bold text-xl mb-1 ${locked ? "text-[#4A5568]" : "text-[#1A2A40]"}`}>{l.title}</div>
                <div className="text-sm text-[#4A5568]">{l.subtitle}</div>
              </Link>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Public "vitrine" of a lesson: readable without login and by crawlers.
// Shows title, objective, vocab, one grammar point + example, then a CTA.
// Never renders reserved content (dialogues, audio, exercises, quiz).
const PublicLessonView = ({ id }) => {
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/lessons/${id}/public`)
      .then((r) => { if (alive) { setData(r.data); setNotFound(false); } })
      .catch(() => { if (alive) setNotFound(true); });
    return () => { alive = false; };
  }, [id]);

  const title = data?.title || "";
  const level = data?.level || "";
  const seoTitle = title
    ? `${title} | Cours de japonais JLPT ${level} | Japonais Pas à Pas`
    : "Cours de japonais | Japonais Pas à Pas";
  const seoDesc = (data?.objective || "Apprenez le japonais pas à pas avec des leçons claires, du vocabulaire et de la grammaire.").slice(0, 155).trim();
  useSEO({
    title: seoTitle,
    description: seoDesc,
    canonical: `https://japonais-pas-a-pas.com/lessons/${id}`,
  });

  // Non-public or unknown lesson: keep today's behavior (login gate).
  if (notFound) return <Navigate to={`/login?next=/lessons/${id}`} replace />;
  if (!data) return <div className="washi-bg min-h-screen"><Navbar /></div>;

  const bucket = findBucketForLesson({ theme: data.theme });

  return (
    <div className="washi-bg min-h-screen" data-testid="lesson-public-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 sm:px-12 py-12">
        <Link to="/lessons" data-testid="back-to-lessons" className="inline-flex items-center gap-2 text-sm font-bold text-[#4A5568] hover:text-[#E94A3F] mb-8">
          <ArrowLeft className="w-4 h-4" /> Toutes les leçons
        </Link>

        {data.emoji && <div className="text-6xl mb-6">{data.emoji}</div>}
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">
          {data.level}{data.duration ? ` · ${data.duration}` : ""}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-3" data-testid="public-lesson-title">
          {data.title}
        </h1>
        {data.subtitle && <p className="text-lg text-[#4A5568] mb-6">{data.subtitle}</p>}

        {data.objective && (
          <div data-testid="public-lesson-objective" className="mb-10 rounded-2xl border-2 border-[#1A2A40] bg-[#FFF5E0] px-5 py-4 flex items-start gap-3">
            <div className="shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[#1A2A40] text-[#FFD166] font-black text-xs flex items-center justify-center">◎</div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-1">Objectif</div>
              <p className="text-[#1A2A40] leading-snug">{data.objective}</p>
            </div>
          </div>
        )}

        {data.vocab?.length > 0 && (
          <div className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-8">
            <h2 className="font-display font-bold text-2xl mb-6 text-[#1A2A40]">Vocabulaire essentiel</h2>
            <div className="space-y-3">
              {data.vocab.map((v, i) => (
                <div key={i} className="flex items-baseline gap-4 flex-wrap bg-[#F9F6F0] rounded-xl p-4 border border-[#1A2A40]/10" data-testid={`public-vocab-${i}`}>
                  <span className="font-jp font-bold text-3xl text-[#1A2A40]">{v.jp}</span>
                  <span className="font-mono text-sm text-[#E94A3F]">{v.romaji}</span>
                  <span className="text-[#4A5568]">, {v.fr}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.grammar?.rule && (
          <div className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-8" data-testid="public-grammar-block">
            <h2 className="font-display font-bold text-2xl mb-3 text-[#1A2A40]">Grammaire</h2>
            <p className="text-[#4A5568] leading-relaxed mb-5">{data.grammar.rule}</p>
            {data.grammar.example && (
              <div className="bg-[#F9F6F0] rounded-xl p-4 border border-[#1A2A40]/10">
                <div className="font-jp font-bold text-xl text-[#1A2A40] mb-1">{data.grammar.example.jp}</div>
                <div className="font-mono text-xs text-[#E94A3F] mb-1">{data.grammar.example.romaji}</div>
                <div className="text-sm text-[#4A5568] italic">{data.grammar.example.fr}</div>
              </div>
            )}
          </div>
        )}

        {/* CTA — the rest of the lesson (exercises, quiz, audio, dialogues, Sensei) is for members */}
        <div className="bg-[#1A2A40] text-white rounded-3xl p-8 mb-10 shadow-brut border-2 border-[#1A2A40] text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 text-[#FFD166] rounded-full px-4 py-1.5 mb-4">
            <Lock className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Suite réservée</span>
          </div>
          <p className="text-white/80 mb-6 max-w-lg mx-auto">
            Exercices, quiz, audio natif, dialogues et Sensei IA vous attendent. Commencez votre essai gratuit de 3 jours, sans engagement.
          </p>
          <Link to="/register" data-testid="public-cta-trial" className="bg-[#E94A3F] text-white font-bold py-4 px-8 rounded-xl shadow-brut hover-lift inline-flex items-center gap-2">
            <Crown className="w-4 h-4" /> Continuer la leçon : essai gratuit 3 jours
          </Link>
        </div>

        {/* Prev / theme / next — same theme */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-t-2 border-[#1A2A40]/10 pt-8">
          {data.prev ? (
            <Link to={`/lessons/${data.prev.id}`} data-testid="public-prev-lesson" className="flex-1 rounded-xl border-2 border-[#1A2A40]/15 hover:border-[#E94A3F] px-4 py-3 text-sm font-bold text-[#1A2A40] transition-colors">
              ← {data.prev.title}
            </Link>
          ) : <div className="flex-1" />}
          {bucket && (
            <Link to={`/lessons/theme/${bucket.slug}`} data-testid="public-theme-link" className="text-center rounded-xl bg-[#FFD166]/40 border-2 border-[#FFD166] px-4 py-3 text-sm font-bold text-[#1A2A40] hover-lift">
              Thème : {bucket.name}
            </Link>
          )}
          {data.next ? (
            <Link to={`/lessons/${data.next.id}`} data-testid="public-next-lesson" className="flex-1 rounded-xl border-2 border-[#1A2A40]/15 hover:border-[#E94A3F] px-4 py-3 text-sm font-bold text-[#1A2A40] text-right transition-colors">
              {data.next.title} →
            </Link>
          ) : <div className="flex-1" />}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const LessonDetail = () => {
  const { id } = useParams();
  const { user, refresh, loading } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [locked, setLocked] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const nav = useNavigate();
  const { speak: ttsSpeak } = useTTS();
  // Wrapper: laisser useTTS résoudre la voix depuis VoiceContext (Atsuko/Alexis)
  // sauf si l'appelant force explicitement une voix (ex: dialogue A/B).
  const speak = (t, voice) => ttsSpeak(t, voice);

  useEffect(() => {
    if (!user) return; // logged-out visitors get the public vitrine (below)
    api.get(`/lessons/${id}`)
      .then((r) => { setLesson(r.data); setLocked(false); })
      .catch((err) => {
        if (err.response?.status === 402) {
          setLocked(true);
          // Fetch lesson metadata from the public list to display the locked card
          api.get("/lessons").then((r) => {
            const found = r.data.data.find((l) => l.id === id);
            if (found) setLesson(found);
          });
        }
      });
  }, [id, user]);

  if (loading) return <div className="washi-bg min-h-screen"><Navbar /></div>;
  if (!user) return <PublicLessonView id={id} />;
  if (!lesson) return <div className="washi-bg min-h-screen"><Navbar /></div>;

  if (locked) {
    return (
      <div className="washi-bg min-h-screen" data-testid="lesson-locked-page">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 sm:px-12 py-20 text-center">
          <div className="text-7xl mb-6 opacity-50 grayscale">{lesson.emoji}</div>
          <div className="inline-flex items-center gap-2 bg-[#1A2A40] text-[#FFD166] rounded-full px-4 py-1.5 mb-4">
            <Lock className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">Leçon Premium</span>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-3">
            {lesson.title}
          </h1>
          <p className="text-lg text-[#4A5568] mb-10">
            Cette leçon fait partie de votre parcours Premium. Démarrez votre essai 3 jours offert
            pour la débloquer (et toutes les autres).
          </p>
          <Link to="/pricing" data-testid="locked-upgrade-btn" className="bg-[#E94A3F] text-white font-bold py-4 px-8 rounded-xl shadow-brut hover-lift inline-flex items-center gap-2">
            <Crown className="w-4 h-4" /> Démarrer mon essai gratuit
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const isDone = user.completed_lessons?.includes(lesson.id);

  const complete = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post("/lessons/complete", { lesson_id: lesson.id });
      await refresh();
      toast.success(data.awarded ? "+25 XP ! Excellent ! 🌸" : "Leçon revue ✓");
      nav("/dashboard");
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setCompleting(false);
    }
  };

  const hasQuiz = Array.isArray(lesson.quiz?.questions) && lesson.quiz.questions.length >= 4;

  const onValidateClick = () => {
    if (!hasQuiz) {
      // Fallback pour leçons sans quiz: comportement historique (1 clic).
      complete();
      return;
    }
    // Avec quiz : le quiz s'ouvre à chaque clic, y compris pour une leçon déjà validée.
    // L'XP n'est jamais attribuée deux fois (garantie côté serveur, /lessons/complete).
    setQuizOpen(true);
  };

  const onQuizSuccess = async () => {
    setQuizOpen(false);
    await complete();
  };

  return (
    <div className="washi-bg min-h-screen" data-testid="lesson-detail-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 sm:px-12 py-12">
        <Link to="/lessons" data-testid="back-to-lessons" className="inline-flex items-center gap-2 text-sm font-bold text-[#4A5568] hover:text-[#E94A3F] mb-8">
          <ArrowLeft className="w-4 h-4" /> Toutes les leçons
        </Link>

        <div className="text-6xl mb-6">{lesson.emoji}</div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">{lesson.level} · {lesson.duration}</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-3">{lesson.title}</h1>
        <p className="text-lg text-[#4A5568] mb-6">{lesson.subtitle}</p>

        {lesson.objective && (
          <div
            data-testid="lesson-objective"
            className="mb-10 rounded-2xl border-2 border-[#1A2A40] bg-[#FFF5E0] px-5 py-4 flex items-start gap-3"
          >
            <div className="shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[#1A2A40] text-[#FFD166] font-black text-xs flex items-center justify-center">
              ◎
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-1">
                Objectif
              </div>
              <p className="text-[#1A2A40] leading-snug">{lesson.objective}</p>
            </div>
          </div>
        )}

        <div className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-8">
          <h2 className="font-display font-bold text-2xl mb-6 text-[#1A2A40]">Vocabulaire essentiel</h2>
          <div className="space-y-3">
            {lesson.vocab.map((v, i) => (
              <div key={i} className="flex items-center justify-between gap-4 bg-[#F9F6F0] rounded-xl p-4 border border-[#1A2A40]/10" data-testid={`vocab-${i}`}>
                <div className="flex items-baseline gap-4 flex-wrap">
                  <span className="font-jp font-bold text-3xl text-[#1A2A40]">{v.jp}</span>
                  <span className="font-mono text-sm text-[#E94A3F]">{v.romaji}</span>
                  <span className="text-[#4A5568]">, {v.fr}</span>
                </div>
                <button onClick={() => speak(v.jp)} data-testid={`speak-vocab-${i}`} className="p-2 rounded-lg bg-white hover:bg-[#FFD166] transition-colors border border-[#1A2A40]/10">
                  <Volume2 className="w-4 h-4 text-[#1A2A40]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {lesson.intro && (
          <div className="bg-[#F3F6F1] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-8">
            <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-2">Intro</div>
            <p className="text-[#1A2A40] leading-relaxed text-lg">{lesson.intro}</p>
          </div>
        )}

        {lesson.grammar && (
          <div className="bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 mb-8" data-testid="grammar-block">
            <h2 className="font-display font-bold text-2xl mb-3 text-[#1A2A40]">Grammaire</h2>
            <p className="text-[#4A5568] leading-relaxed mb-5">{lesson.grammar.rule}</p>
            <div className="space-y-3">
              {(lesson.grammar.examples || []).map((ex, i) => (
                <div key={i} className="bg-[#F9F6F0] rounded-xl p-4 border border-[#1A2A40]/10 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-jp font-bold text-xl text-[#1A2A40] mb-1">{ex.jp}</div>
                    <div className="font-mono text-xs text-[#E94A3F] mb-1">{ex.romaji}</div>
                    <div className="text-sm text-[#4A5568] italic">{ex.fr}</div>
                  </div>
                  <button
                    onClick={() => speak(ex.jp)}
                    data-testid={`speak-example-${i}`}
                    className="shrink-0 p-2 rounded-lg bg-white hover:bg-[#FFD166] transition-colors border border-[#1A2A40]/10"
                    aria-label="Écouter l'exemple"
                  >
                    <Volume2 className="w-4 h-4 text-[#1A2A40]" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {lesson.dialogue && (() => {
          // Normalise A/B en se basant sur l'ordre d'apparition des speakers distincts.
          // Fiabilise les cas où les données contiennent "A (serveur)", "学生A", "教授", etc.
          const lines = lesson.dialogue.lines || [];
          const speakerKeys = [...new Set(lines.map((l) => l.speaker))];
          const roleOf = (s) => (speakerKeys.indexOf(s) === 0 ? "A" : "B");
          return (
          <div className="bg-[#1A2A40] text-white rounded-3xl p-8 mb-8 shadow-brut border-2 border-[#1A2A40]" data-testid="dialogue-block">
            <div className="text-xs font-bold uppercase tracking-widest text-[#FFD166] mb-2">Dialogue · {lesson.dialogue.title}</div>
            <div className="space-y-3 mt-4">
              {lines.map((ln, i) => {
                const role = roleOf(ln.speaker);
                return (
                <div key={i} className={`flex ${role === "B" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${
                    role === "A" ? "bg-white/10 border border-white/20" : "bg-[#E94A3F] text-white"
                  }`}>
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="text-[10px] uppercase tracking-widest text-[#FFD166]">{ln.speaker}</div>
                      <button
                        onClick={() => speak(ln.jp, role === "B" ? "male" : "female")}
                        data-testid={`speak-dialogue-${i}`}
                        className="shrink-0 p-1.5 rounded-md bg-white/10 hover:bg-[#FFD166] hover:text-[#1A2A40] transition-colors"
                        aria-label="Écouter la réplique"
                      >
                        <Volume2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="font-jp font-bold text-lg leading-tight">{ln.jp}</div>
                    <div className="font-mono text-[11px] opacity-70 mt-1">{ln.romaji}</div>
                    <div className="text-sm italic opacity-90 mt-1">{ln.fr}</div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
          );
        })()}

        <div className="bg-[#FFD166]/30 border-2 border-[#FFD166] rounded-2xl p-6 mb-8 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-[#1A2A40] mt-0.5 shrink-0" />
          <p className="text-sm text-[#1A2A40]">
            <strong>Astuce :</strong> {lesson.tip || "Récitez ces mots à voix haute 3 fois avant de valider. Puis consultez Sensei IA pour un exercice de mise en pratique."}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onValidateClick}
            disabled={completing}
            data-testid="complete-lesson-btn"
            className={`flex-1 font-bold py-4 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
              isDone ? "bg-[#8CA07A] text-white" : "bg-[#E94A3F] text-white"
            }`}
          >
            <Check className="w-4 h-4" />{" "}
            {isDone
              ? (hasQuiz ? "Refaire le quiz (révision, sans XP)" : "Marquer revu")
              : (hasQuiz ? "Valider la leçon (quiz)" : "Valider la leçon (+25 XP)")}
          </button>
          <Link
            to={lesson.id === "l1" ? "/tutor?lesson=l1" : "/tutor"}
            data-testid="practice-with-tutor"
            className="flex-1 bg-[#1A2A40] text-white font-bold py-4 rounded-xl text-center hover-lift"
          >
            Pratiquer avec Sensei →
          </Link>
        </div>
        {quizOpen && hasQuiz && (
          <LessonQuiz
            quiz={lesson.quiz}
            speak={speak}
            alreadyCompleted={isDone}
            onSuccess={onQuizSuccess}
            onCancel={() => setQuizOpen(false)}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};
